import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily, atomWithRefresh } from "jotai/utils";

import { $accessTokenFunc, $isAuthenticated } from "../authenication";
import { $docsApi, DocsApiError } from "../docsapi/docsapi.atom";
import {
	WaStatusAccessTokenMissingError,
	WaStatusAuthRequiredError,
	WaStatusFetchError,
	WaStatusSyncError,
	WaStatusWorkspaceRequiredError,
	WcAccessTokenMissingError,
	WcAuthRequiredError,
	WcSessionCreationError,
	WcWorkspaceRequiredError,
} from "./workspace.errors";
import { type Workspace } from "./workspace.types";

type CheckoutStatus =
	| { status: "idle" }
	| { status: "pending" }
	| { status: "success"; url: string }
	| { status: "error"; error: DocsApiError };

export const $workspaceCheckoutState = atomFamily((workspaceId: string | undefined) => {
	const baseAtom = atom<CheckoutStatus>({ status: "idle" });
	baseAtom.debugLabel = `workspaceCheckoutState/${workspaceId ?? "unknown"}`;
	return baseAtom;
}, isEqual);

export const $createWorkspaceCheckout = atom(
	null,
	async (get, set, workspace: Workspace | undefined) => {
		if (!workspace) {
			throw new WcWorkspaceRequiredError();
		}

		const statusAtom = $workspaceCheckoutState(workspace.id);
		set(statusAtom, { status: "pending" });

		if (!get($isAuthenticated)) {
			throw new WcAuthRequiredError();
		}

		const docsApi = get($docsApi);
		const token = await get($accessTokenFunc).getAccessTokenSilently?.();
		if (!token) {
			throw new WcAccessTokenMissingError();
		}

		try {
			const result = await docsApi.payments.createWorkspaceCheckout(workspace, token);
			set(statusAtom, { status: "success", url: result.url });
			return result;
		} catch (error) {
			const checkoutError =
				error instanceof DocsApiError ? error : new WcSessionCreationError(error);
			set(statusAtom, { status: "error", error: checkoutError });
			throw checkoutError;
		}
	}
);
$createWorkspaceCheckout.debugLabel = "createWorkspaceCheckout";

export type WorkspaceAccountStatus = string | null | DocsApiError;

export const $workspaceAccountStatus = atomFamily((workspace: Workspace | undefined) => {
	const statusAtom = atomWithRefresh(async (get): Promise<WorkspaceAccountStatus> => {
		if (!workspace || !get($isAuthenticated)) {
			if (!workspace) {
				throw new WaStatusWorkspaceRequiredError();
			}
			throw new WaStatusAuthRequiredError();
		}

		const docsApi = get($docsApi);
		const token = await get($accessTokenFunc).getAccessTokenSilently?.();
		if (!token) {
			throw new WaStatusAccessTokenMissingError();
		}

		await docsApi.payments.customer.sync(workspace, token).catch((error) => {
			throw new WaStatusSyncError(error);
		});

		try {
			const { status } = await docsApi.payments.customer.status(workspace, token);
			return status ?? null;
		} catch (error) {
			if (error instanceof DocsApiError) {
				throw error;
			}
			throw new WaStatusFetchError(error);
		}
	});

	statusAtom.debugLabel = `workspaceAccountStatus/${workspace?.id ?? "unknown"}`;
	return statusAtom;
}, isEqual);
